import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ShoppingBag } from 'lucide-react';
import { navigation } from '../data/mock';
import { Button } from '@/components/ui/button';
import Logo from './Logo';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-xl shadow-[0_4px_24px_rgba(10,37,64,0.06)] border-b border-slate-100'
          : 'bg-white/70 backdrop-blur-md'
      }`}
    >
      <div className="container-pn flex items-center justify-between h-20">
        {/* Logo */}
        <Logo to="/" imgClassName="h-8 md:h-9" testId="navbar-logo" />

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {navigation.map((item) => {
            const active = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`relative px-4 py-2 text-sm font-semibold tracking-tight transition-colors ${
                  active ? 'text-[#0A2A63]' : 'text-slate-600 hover:text-[#0A2A63]'
                }`}
              >
                {item.name}
                {active && (
                  <span className="absolute left-4 right-4 -bottom-0.5 h-0.5 bg-[#FF4E50] rounded-full" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* CTAs */}
        <div className="hidden lg:flex items-center gap-3">
          <a
            href="tel:8003535546"
            className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#0A2A63] transition-colors btn-transition"
          >
            <Phone className="h-4 w-4" />
            <span className="font-semibold">(800) 353-5546</span>
          </a>
          <Link to="/shop">
            <Button className="bg-[#0A2A63] hover:bg-[#061a3f] text-white rounded-full px-5 h-10 font-semibold btn-transition shadow-sm hover:shadow-md">
              <ShoppingBag className="h-4 w-4 mr-2" />
              Shop
            </Button>
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          className="lg:hidden p-2 rounded-md text-[#0A2A63]"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-slate-100">
          <nav className="container-pn py-4 flex flex-col gap-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="px-3 py-3 rounded-lg text-slate-700 hover:bg-slate-50 hover:text-[#0A2A63] font-semibold"
              >
                {item.name}
              </Link>
            ))}
            <a
              href="tel:8003535546"
              className="px-3 py-3 rounded-lg flex items-center gap-2 text-[#0A2A63] font-semibold"
            >
              <Phone className="h-4 w-4" /> (800) 353-5546
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
